// Средства измерения и контроля
import React, { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import {
  CFormLabel,
  CFormInput,
  CFormTextarea,
  CForm,
  CContainer,
  CRow,
  CCol,
  CButton,
  CTable,
  CTableHead,
  CTableBody,
  CTableRow,
  CTableHeaderCell,
  CTableDataCell,
  CModal,
  CModalHeader,
  CModalTitle,
  CModalBody,
  CFormCheck
} from '@coreui/react';
import PropTypes from 'prop-types';
import CIcon from '@coreui/icons-react';
import { cilStar, cilPlus, cilMinus, cilArrowTop, cilArrowBottom } from '@coreui/icons';

const MeasuringInstrumentsTab = forwardRef(({ initialData, onSave }, ref) => {
  const [instruments, setInstruments] = useState([]);
  const [selectedInstrumentId, setSelectedInstrumentId] = useState(null);
  const [showReportInfo, setShowReportInfo] = useState(false);
  
  // Модальное окно для справочника работ
  const [showModal, setShowModal] = useState(false);
  const [worksData, setWorksData] = useState([]);
  const [selectedModalWork, setSelectedModalWork] = useState(null);
  const [loading, setLoading] = useState(false);

  // Справочник для средств измерения и контроля
  const measuringInstruments = [
    'Лазерный дальномер',
    'Нивелир',
    'Теодолит',
    'Ультразвуковой дефектоскоп',
    'Толщиномер',
    'Штангенциркуль',
    'Микрометр',
    'Термограф',
    'Влагомер',
    'Прочностьметр'
  ];

  useEffect(() => {
    if (initialData) {
      setInstruments(initialData.instruments || []);
      setShowReportInfo(initialData.showReportInfo || false);
    }
  }, [initialData]);

  // Загрузка данных работ из API (заглушка)
  const fetchWorksData = async () => {
    setLoading(true);
    try {
      // Заглушка для имитации загрузки данных
      setTimeout(() => {
        const mockData = [
          { ID: 1, Name: 'Визуальный осмотр конструкций', Description: 'Осмотр видимых дефектов и повреждений' },
          { ID: 2, Name: 'Измерение геометрических параметров', Description: 'Замеры размеров и отклонений' },
          { ID: 3, Name: 'Контроль прочности материалов', Description: 'Испытания на прочность и твердость' },
          { ID: 4, Name: 'Дефектоскопия', Description: 'Обнаружение внутренних дефектов' },
          { ID: 5, Name: 'Измерение влажности', Description: 'Контроль влажности материалов' },
          { ID: 6, Name: 'Температурный контроль', Description: 'Измерение температурных параметров' },
          { ID: 7, Name: 'Вибродиагностика', Description: 'Контроль вибрационных характеристик' }
        ];
        setWorksData(mockData);
        setLoading(false);
      }, 500);
    } catch (error) {
      console.error('Ошибка загрузки данных работ:', error);
      setWorksData([]);
      setLoading(false);
    }
  };

  const handleOpenModal = () => {
    setShowModal(true);
    setSelectedModalWork(null);
    fetchWorksData();
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedModalWork(null);
  };

  const handleWorkDoubleClick = (work) => {
    const newId = Math.max(...instruments.map(item => item.id), 0) + 1;
    const newNumber = instruments.length + 1;
    
    const newInstrument = {
      id: newId,
      number: newNumber,
      workName: work.Name,
      workContent: work.Description || '',
      measuringInstruments: ''
    };
    
    const updatedInstruments = [...instruments, newInstrument];
    setInstruments(updatedInstruments);
    setSelectedInstrumentId(newId);
    handleCloseModal();
  };

  const handleInstrumentClick = (id) => {
    setSelectedInstrumentId(id);
  };

  const addInstrument = () => {
    handleOpenModal();
  };

  const removeInstrument = () => {
    if (selectedInstrumentId && instruments.length > 0) {
      const confirmDelete = window.confirm('Удалить выбранную запись?');
      
      if (confirmDelete) {
        const updatedInstruments = instruments.filter(item => item.id !== selectedInstrumentId);
        
        // Перенумеровываем оставшиеся записи
        const renumberedInstruments = updatedInstruments.map((item, index) => ({
          ...item,
          number: index + 1
        }));
        
        setInstruments(renumberedInstruments);
        setSelectedInstrumentId(renumberedInstruments.length > 0 ? renumberedInstruments[0].id : null);
      }
    } else if (instruments.length > 0) {
      const confirmDelete = window.confirm('Удалить последнюю запись?');
      
      if (confirmDelete) {
        const updatedInstruments = instruments.slice(0, -1);
        
        // Перенумеровываем оставшиеся записи
        const renumberedInstruments = updatedInstruments.map((item, index) => ({
          ...item,
          number: index + 1
        }));
        
        setInstruments(renumberedInstruments);
        setSelectedInstrumentId(renumberedInstruments.length > 0 ? renumberedInstruments[renumberedInstruments.length - 1].id : null);
      }
    }
  };

  const moveInstrumentUp = () => {
    if (selectedInstrumentId && instruments.length > 1) {
      const index = instruments.findIndex(item => item.id === selectedInstrumentId);
      if (index > 0) {
        const newInstruments = [...instruments];
        [newInstruments[index - 1], newInstruments[index]] = [newInstruments[index], newInstruments[index - 1]];
        
        // Обновляем номера
        const renumberedInstruments = newInstruments.map((item, idx) => ({
          ...item,
          number: idx + 1
        }));
        
        setInstruments(renumberedInstruments);
      }
    }
  };

  const moveInstrumentDown = () => {
    if (selectedInstrumentId && instruments.length > 1) {
      const index = instruments.findIndex(item => item.id === selectedInstrumentId);
      if (index < instruments.length - 1) {
        const newInstruments = [...instruments];
        [newInstruments[index], newInstruments[index + 1]] = [newInstruments[index + 1], newInstruments[index]];
        
        // Обновляем номера
        const renumberedInstruments = newInstruments.map((item, idx) => ({
          ...item,
          number: idx + 1
        }));
        
        setInstruments(renumberedInstruments);
      }
    }
  };

  const markAsMain = () => {
    if (selectedInstrumentId) {
      setInstruments(instruments.map(item => ({
        ...item,
        isMain: item.id === selectedInstrumentId
      })));
    }
  };

  const handleFieldChange = (id, field, value) => {
    setInstruments(instruments.map(item => 
      item.id === id ? { ...item, [field]: value } : item
    ));
  };

  const handleShowReportInfoChange = (e) => {
    setShowReportInfo(e.target.checked);
  };

  const getFormDataForSave = () => {
    return {
      instruments,
      showReportInfo
    };
  };

  useImperativeHandle(ref, () => ({
    save: async () => {
      if (onSave) {
        const formData = getFormDataForSave();
        await onSave(formData);
      }
    },
    getFormData: getFormDataForSave
  }));

  return (
    <CContainer fluid className="measuring-instruments-tab-container">
      <CForm>
        {/* Заголовок */}
        <CRow className="mb-3">
          <CCol>
            <CFormLabel className="fw-bold">
              Инструментальное обследование конструкций опоры выполнялось с помощью измерительных приборов и средств контроля:
            </CFormLabel>
            <div className="border-bottom mt-2 mb-3"></div>
          </CCol>
        </CRow>

        {/* Кнопки управления и чекбокс */}
        <CRow className="mb-3 align-items-center">
          <CCol md={8}>
            <div className="d-flex gap-2">
              {/* <CButton 
                color="light" 
                variant="outline" 
                size="sm" 
                className="text-dark border"
                onClick={addInstrument}
              >
                <CIcon icon={cilPlus} className="me-1" />
                +
              </CButton> */}
              <CButton 
                color="light" 
                variant="outline" 
                size="sm" 
                className="text-dark border"
                onClick={removeInstrument}
              >
                <CIcon icon={cilMinus} className="me-1" />
                -
              </CButton>
              <CButton 
                color="light" 
                variant="outline" 
                size="sm" 
                className="text-dark border"
                onClick={moveInstrumentUp}
              >
                <CIcon icon={cilArrowTop} className="me-1" />
                ↑
              </CButton>
              <CButton 
                color="light" 
                variant="outline" 
                size="sm" 
                className="text-dark border"
                onClick={moveInstrumentDown}
              >
                <CIcon icon={cilArrowBottom} className="me-1" />
                ↓
              </CButton>
              <CButton 
                color="light" 
                variant="outline" 
                size="sm" 
                className="text-dark border"
                onClick={markAsMain}
              >
                <CIcon icon={cilStar} className="me-1" />
                ★
              </CButton>
            </div>
          </CCol>
          <CCol md={4} className="text-end">
            <CFormCheck
              id="showReportInfo"
              label="Выводить в отчет информацию о проверке"
              checked={showReportInfo}
              onChange={handleShowReportInfoChange}
              className="d-inline-block"
            />
          </CCol>
        </CRow>

        {/* Таблица средств измерения и контроля */}
        <CRow className="mb-3">
          <CCol>
            <CTable bordered small className="mb-0">
              <CTableHead>
                <CTableRow>
                  <CTableHeaderCell className="border text-center" style={{ width: '50px' }}>№</CTableHeaderCell>
                  <CTableHeaderCell className="border text-center" style={{ width: '250px' }}>Наименование работ</CTableHeaderCell>
                  <CTableHeaderCell className="border text-center" style={{ width: '300px' }}>Содержание работ</CTableHeaderCell>
                  <CTableHeaderCell className="border text-center">Наименование средств измерения и контроля</CTableHeaderCell>
                </CTableRow>
              </CTableHead>
              <CTableBody>
                {instruments.map((instrument) => (
                  <CTableRow 
                    key={instrument.id} 
                    onClick={() => handleInstrumentClick(instrument.id)}
                    className={selectedInstrumentId === instrument.id ? 'table-primary' : ''}
                    style={{ cursor: 'pointer' }}
                  >
                    <CTableDataCell className="border text-center" style={{ verticalAlign: 'middle' }}>
                      <div className="d-flex align-items-center justify-content-center">
                        {instrument.isMain && (
                          <CIcon icon={cilStar} className="text-warning me-1" size="sm" />
                        )}
                        {instrument.number}
                      </div>
                    </CTableDataCell>
                    <CTableDataCell className="border p-0">
                      <CFormTextarea
                        value={instrument.workName}
                        onChange={(e) => handleFieldChange(instrument.id, 'workName', e.target.value)}
                        placeholder="Наименование работ"
                        className="border-0 w-100"
                        style={{ 
                          borderRadius: '0', 
                          resize: 'none',
                          minHeight: '38px',
                          fontSize: '0.875rem'
                        }}
                        rows={1}
                        onInput={(e) => {
                          e.target.style.height = 'auto';
                          e.target.style.height = e.target.scrollHeight + 'px';
                        }}
                      />
                    </CTableDataCell>
                    <CTableDataCell className="border p-0">
                      <CFormTextarea
                        value={instrument.workContent}
                        onChange={(e) => handleFieldChange(instrument.id, 'workContent', e.target.value)}
                        placeholder="Содержание работ"
                        className="border-0 w-100"
                        style={{ 
                          borderRadius: '0', 
                          resize: 'none',
                          minHeight: '38px',
                          fontSize: '0.875rem'
                        }}
                        rows={1}
                        onInput={(e) => {
                          e.target.style.height = 'auto';
                          e.target.style.height = e.target.scrollHeight + 'px';
                        }}
                      />
                    </CTableDataCell>
                    <CTableDataCell className="border p-0">
                      <CFormTextarea
                        value={instrument.measuringInstruments}
                        onChange={(e) => handleFieldChange(instrument.id, 'measuringInstruments', e.target.value)}
                        placeholder="Средства измерения и контроля"
                        className="border-0 w-100"
                        style={{ 
                          borderRadius: '0', 
                          resize: 'none',
                          minHeight: '38px',
                          fontSize: '0.875rem'
                        }}
                        rows={1}
                        onInput={(e) => {
                          e.target.style.height = 'auto';
                          e.target.style.height = e.target.scrollHeight + 'px';
                        }}
                      />
                    </CTableDataCell>
                  </CTableRow>
                ))}
                {instruments.length === 0 && (
                  <CTableRow>
                    <CTableDataCell 
                      colSpan="4" 
                      className="border text-center text-muted py-3"
                    >
                      Нет добавленных записей. Нажмите "+" чтобы добавить.
                    </CTableDataCell>
                  </CTableRow>
                )}
              </CTableBody>
            </CTable>
          </CCol>
        </CRow>
      </CForm>

      {/* Модальное окно справочника работ */}
      <CModal visible={showModal} onClose={handleCloseModal} size="lg">
        <CModalHeader>
          <CModalTitle>Справочник: Виды работ</CModalTitle>
        </CModalHeader>
        <CModalBody>
          {loading ? (
            <div className="text-center">Загрузка данных работ...</div>
          ) : (
            <>
              <CTable bordered small className="mb-3">
                <CTableHead>
                  <CTableRow>
                    <CTableHeaderCell className="border">Наименование работ</CTableHeaderCell>
                    <CTableHeaderCell className="border">Содержание работ</CTableHeaderCell>
                  </CTableRow>
                </CTableHead>
                <CTableBody>
                  {worksData.map((work, index) => (
                    <CTableRow 
                      key={work.ID || index}
                      onDoubleClick={() => handleWorkDoubleClick(work)}
                      onClick={() => setSelectedModalWork(work)}
                      className={selectedModalWork?.ID === work.ID ? 'table-primary' : ''}
                      style={{ cursor: 'pointer' }}
                    >
                      <CTableDataCell 
                        className="border"
                        style={{ 
                          wordWrap: 'break-word',
                          whiteSpace: 'normal'
                        }}
                      >
                        {work.Name}
                      </CTableDataCell>
                      <CTableDataCell 
                        className="border"
                        style={{ 
                          wordWrap: 'break-word',
                          whiteSpace: 'normal'
                        }}
                      >
                        {work.Description}
                      </CTableDataCell>
                    </CTableRow>
                  ))}
                </CTableBody>
              </CTable>
              <div className="text-muted small">
                💡 Для добавления: двойной клик по строке
              </div>
            </>
          )}
        </CModalBody>
      </CModal>
    </CContainer>
  );
});

MeasuringInstrumentsTab.propTypes = {
  initialData: PropTypes.shape({
    instruments: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.number.isRequired,
        number: PropTypes.number.isRequired,
        workName: PropTypes.string.isRequired,
        workContent: PropTypes.string,
        measuringInstruments: PropTypes.string,
        isMain: PropTypes.bool
      })
    ),
    showReportInfo: PropTypes.bool
  }),
  onSave: PropTypes.func
};

MeasuringInstrumentsTab.defaultProps = {
  initialData: null,
  onSave: null
};

export default MeasuringInstrumentsTab;